<template>
    <div
        :class="$style.details"
        data-interface-section="game-details"
    >
        <div :class="$style.primary">
            <div :class="$style.primary_main">
                <Breadcrumbs
                    v-if="!appStore.isAppEnv"
                    :breadcrumbs="props.breadcrumbs"
                />
                <Typography
                    is="h1"
                    id="game-title"
                    type="header"
                    size="m"
                    :responsive="false"
                >
                    {{ props.game.title }}
                </Typography>
                <MetaInformation
                    :game="props.game"
                    :categoriesInfo="props.categoriesInfo"
                />
                <div
                    v-if="gameTags.length"
                    :class="$style.tags"
                >
                    <component
                        :is="index > 5 ? SeoOnly : 'div'"
                        v-for="(tag, index) in gameTags"
                        :key="tag.id"
                    >
                        <TagButton
                            :to="tag.href"
                            :name="tag.title"
                            :count="tag.totalCount"
                        />
                    </component>
                </div>
            </div>
            <Leaderboard
                v-if="props.leaderboard.entries.length"
                :entries="props.leaderboard.entries"
                :totalPlayers="props.leaderboard.totalPlayers"
                :hru="props.game.hru"
                :class="$style.leaderboard_override"
            />
            <Actions />
        </div>
        <SeoBlock :game="props.game" />
    </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import Breadcrumbs from '@/components/Breadcrumbs.vue'
import SeoOnly from '@/components/SeoOnly.vue'
import Typography from '@/components/Typography.vue'
import { getSubCategories } from '@/pages/Games/utils'
import { useAppStore } from '@/store/app-store'
import type { APIGameType, GameCategoriesInfoType, GameLeaderboard } from '@/types'

import Actions from './Actions.vue'
import Leaderboard from './leaderboard/Leaderboard.vue'
import MetaInformation from './MetaInformation.vue'
import SeoBlock from './SeoBlock.vue'
import TagButton from './TagButton.vue'

const props = defineProps<{
    game: APIGameType
    categoriesInfo: GameCategoriesInfoType
    breadcrumbs: { href: string | RouteLocationRaw; title: string }[]
    leaderboard: GameLeaderboard
}>()

const appStore = useAppStore()

const gameTags = computed(() => getSubCategories('tag', props.categoriesInfo))
</script>
<style module>
.details {
    container-type: inline-size;
    border-radius: 16px;
    background: var(--carbon-300);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* minmax(0, 1fr): an implicit auto column would size to max-content and let a nowrap breadcrumb widen the card */
.primary {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--gap-20);
}

.primary_main {
    display: flex;
    flex-direction: column;
    gap: var(--gap-20);
    min-width: 0;
}

.leaderboard_override {
    min-width: 0;
}

.tags {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 8px 0;
}

.tags > *:not(:last-child) {
    margin-right: 6px;
}

@container (min-width: 668px) {
    .primary {
        grid-auto-columns: 280px;
        column-gap: var(--gap-24);
    }

    /* without this Actions auto-places into column 2 when there is no leaderboard */
    .primary > * {
        grid-column: 1;
    }

    .leaderboard_override {
        grid-column: 2;
        grid-row: 1 / span 2;
        align-self: start;
    }
}
</style>
